import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ApiProvider } from "@src/store/ApiProvider";
import axios from "axios";
import { ICredential } from "./getCredentials";
import { withPasswordManagerRefresh } from "./passwordManagerRequest";

export interface IUpdateNotesParams {
	credential: ICredential;
	newNote: string;
}

interface IUpdateNotesState {
	loading: boolean;
	error: string | null;
}

const initialState: IUpdateNotesState = {
	loading: false,
	error: null,
};

const getAuthHeaders = () => {
	const token = localStorage.getItem("PasswordManager_accessToken") ?? "";
	return {
		Authorization: token ? `Bearer ${token}` : undefined,
		passwordManagerAccessToken: token || undefined,
		"Content-Type": "application/json",
	};
};

const getExternalUrl = () => {
	const baseUrl = ApiProvider.default.passwordManagerBaseURL || "https://qa.stagefront.com/password-manager/api";
	return `${baseUrl.replace(/\/$/, "")}/Credentials`;
};

export const updateNotes = createAsyncThunk<undefined, IUpdateNotesParams>(
	"passwordManager/updateNotes",
	async ({ credential, newNote }, { rejectWithValue, dispatch }) => {
		try {
			const noteToAdd = newNote?.trim();
			if (!noteToAdd) return undefined;

			console.log("Preparing to update notes for credential:", credential);
			// Password is required by the endpoint; fetch it first
			const passwordRes = await withPasswordManagerRefresh(dispatch, () =>
				axios.post<Array<{ credentialId: number; password: string }>>(
					`${getExternalUrl()}/Passwords`,
					`[ ${credential.credentialId} ]`,
					{
						headers: getAuthHeaders(),
					},
				),
			);

			const passwordEntry = Array.isArray(passwordRes?.data) ? passwordRes.data[0] : undefined;
			const password = passwordEntry?.password ?? "";

			const body = [
				{
					companyId: credential.companyId,
					vendorId: credential.vendorId,
					username: credential.username,
					password,
					notes: noteToAdd,
					credentialId: credential.credentialId,
					errorMessage: credential.errorMessage ?? "",
				},
			];

			await withPasswordManagerRefresh(dispatch, () =>
				axios.put(getExternalUrl(), body, {
					headers: getAuthHeaders(),
				}),
			);
			return undefined;
		} catch (error: unknown) {
			const message = error instanceof Error ? error.message : "Failed to update notes";
			return rejectWithValue(message);
		}
	},
);

const updateNotesSlice = createSlice({
	name: "passwordManager/updateNotes",
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(updateNotes.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(updateNotes.fulfilled, (state) => {
				state.loading = false;
				state.error = null;
			})
			.addCase(updateNotes.rejected, (state, action) => {
				state.loading = false;
				state.error = (action.payload as string) || action.error.message || "Failed to update notes";
			});
	},
});

export const updateNotesReducer = updateNotesSlice.reducer;
